import { Box } from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import LargeCard from '@/components/LargeCard';
import useGridTheme from '@/hooks/useGridTheme';
import { caseDetailsAtom } from '@/pages/Case/Case.store';
import { getCasePriority } from '@/pages/Case/Case.utils';
import { ticketAtom } from '@/pages/Ticket/Ticket.store';

import { TicketResponseActionsDialog } from '../TicketResponseActionsDialog';
import {
  RESPONSE_ACTION_COLUMNS,
  VISIBLE_RESPONSE_ACTIONS
} from './TicketResponseActions.constants';
import { useFetchResponseActionMatrix } from './TicketResponseActions.store';
import { processTableData } from './TicketResponseActions.utils';

const TicketResponseActions = () => {
  const [{ ticket_response_actions, ticket_priority, organization, id }] =
    useAtom(ticketAtom);
  const [caseDetails] = useAtom(caseDetailsAtom);
  const gridTheme = useGridTheme();
  const { pathname } = useLocation();
  const isTicketsPage = pathname.includes('/ticket');
  const [isResponseActionDialogOpen, setIsResponseActionDialogOpen] =
    useState(false);
  const [selectedResponseAction, setSelectedResponseAction] = useState(null);

  const casePriority = getCasePriority(
    caseDetails?.ai_priority,
    caseDetails?.soc_priority
  ) as string;

  const priority = useMemo(
    () =>
      isTicketsPage
        ? ((ticket_priority as { name?: string } | null)?.name ?? '')
        : (casePriority ?? ''),
    [isTicketsPage, ticket_priority, casePriority]
  );
  const organizationLabel = useMemo(
    () =>
      isTicketsPage
        ? typeof organization === 'string'
          ? organization
          : (organization as { label?: string } | null)?.label
        : caseDetails?.organization?.label,
    [isTicketsPage, organization, caseDetails?.organization?.label]
  );
  const keyToInvalidate = useMemo(
    () => (isTicketsPage ? `tickets/${id}` : `cases/${caseDetails?.soc_id}`),
    [isTicketsPage, id, caseDetails?.soc_id]
  );

  const { data: responseActionMatrix } =
    useFetchResponseActionMatrix(organizationLabel);

  const tableData = isTicketsPage
    ? ticket_response_actions || []
    : caseDetails?.response_actions || [];

  const processedTableData = useMemo(
    () => processTableData(tableData),
    [tableData]
  );

  const responseActionsTableTheme = gridTheme.withParams({
    wrapperBorder: true
  });

  const responseActionColumns = useMemo(() => {
    return RESPONSE_ACTION_COLUMNS.map((col) => {
      const column = col as ColDef;

      if (column.field !== 'response_action_trigger') {
        return column;
      }

      return {
        ...column,
        cellRendererParams: (params: ICellRendererParams) => {
          const actionType = params?.data?.action_type;
          const currentStatus = params?.data?.current_status;
          const isDisabled =
            responseActionMatrix?.by_interaction?.disabled?.by_priority?.[
              priority
            ]?.[actionType] ||
            responseActionMatrix?.by_interaction?.not_available?.by_priority?.[
              priority
            ]?.[actionType] ||
            !(
              currentStatus && VISIBLE_RESPONSE_ACTIONS.includes(currentStatus)
            );

          return {
            disabled: Boolean(isDisabled),
            handleRunActionClick: () => {
              setIsResponseActionDialogOpen(true);
              setSelectedResponseAction(params?.data);
            }
          };
        }
      };
    });
  }, [priority, responseActionMatrix]);

  return (
    <LargeCard title="Response Actions">
      <Box sx={{ width: '100%' }}>
        <AgGridReact
          rowData={processedTableData}
          columnDefs={responseActionColumns}
          domLayout="autoHeight"
          theme={responseActionsTableTheme}
          headerHeight={30}
          suppressCellFocus
        />
      </Box>
      {isResponseActionDialogOpen && (
        <TicketResponseActionsDialog
          open={isResponseActionDialogOpen}
          setIsResponseActionDialogOpen={setIsResponseActionDialogOpen}
          keyToInvalidate={keyToInvalidate}
          selectedResponseAction={selectedResponseAction}
        />
      )}
    </LargeCard>
  );
};

export default TicketResponseActions;
